import { AppsFlyer } from '../plugins/apps_flyer_plugin.js';
import { analytics, AnalyticsPropertyKey } from '../analytics/index.js';
import { OfferEventType } from '../api/dto/offers.dto.js';
import type { OfferDto } from '../api/dto/offers.dto.js';
import { GlobalSettings } from '../global_settings.js';

enum OfferAnalyticsProvider
{
  AppsFlyer = 'appsflyer',
  Athena    = 'athena',
}

enum OfferStep
{
  PlacementImpression = 'placement_impression',
  Shown            = 'shown',
  Clicked          = 'clicked',
  Close            = 'close',
  PriceLoaded      = 'price_loaded',
  PriceUnavailable = 'price_unavailable',
  Purchase         = 'purchase',
  FreeClaim        = 'free_claim',
}

enum OfferPurchaseStatus
{
  Started = 'started',
  Success = 'success',
  Failed  = 'failed',
}

const OFFER_STEP_BY_EVENT: Record<OfferEventType, OfferStep> = {
  [OfferEventType.PlacementImpression]: OfferStep.PlacementImpression,
  [OfferEventType.Shown]:            OfferStep.Shown,
  [OfferEventType.Clicked]:          OfferStep.Clicked,
  [OfferEventType.Close]:            OfferStep.Close,
  [OfferEventType.PriceLoaded]:      OfferStep.PriceLoaded,
  [OfferEventType.PriceUnavailable]: OfferStep.PriceUnavailable,
  [OfferEventType.PurchaseStarted]:  OfferStep.Purchase,
  [OfferEventType.PurchaseSuccess]:  OfferStep.Purchase,
  [OfferEventType.PurchaseFailed]:   OfferStep.Purchase,
  [OfferEventType.FreeClaim]:        OfferStep.FreeClaim,
};

const PURCHASE_STATUS_BY_EVENT: Partial<Record<OfferEventType, OfferPurchaseStatus>> = {
  [OfferEventType.PurchaseStarted]: OfferPurchaseStatus.Started,
  [OfferEventType.PurchaseSuccess]: OfferPurchaseStatus.Success,
  [OfferEventType.PurchaseFailed]:  OfferPurchaseStatus.Failed,
};

const APPS_FLYER_PURCHASE_EVENT = 'af_purchase';
const OFFER_SHOWN_EVENT = 'offer_shown';
const OFFER_ACCEPTED_EVENT = 'offer_accepted';
const DEFAULT_APPS_FLYER_EVENT_BY_TYPE: Record<OfferEventType, string> = {
  [OfferEventType.PlacementImpression]: 'offer_placement_impression',
  [OfferEventType.Shown]: 'offer_shown',
  [OfferEventType.Clicked]: 'offer_clicked',
  [OfferEventType.Close]: 'offer_closed',
  [OfferEventType.PriceLoaded]: 'offer_price_loaded',
  [OfferEventType.PriceUnavailable]: 'offer_price_unavailable',
  [OfferEventType.PurchaseStarted]: 'offer_purchase_started',
  [OfferEventType.PurchaseSuccess]: 'offer_purchase_success',
  [OfferEventType.PurchaseFailed]: 'offer_purchase_failed',
  [OfferEventType.FreeClaim]: 'offer_free_claim',
};

export interface OfferPriceAnalyticsContext
{
  productid?: string;
  formatted_price?: string;
  currency?: string;
  price_micros?: number | string;
}

function isObject( value: unknown ): value is Record<string, unknown>
{
  return !!value && typeof value === 'object' && !Array.isArray( value );
}

function readEventName( offer: OfferDto, event_type: OfferEventType, provider: OfferAnalyticsProvider ): string
{
  const data = offer.data ?? {};
  const analytics_cfg = isObject( data.analytics ) ? data.analytics : {};
  const events = isObject( analytics_cfg.events ) ? analytics_cfg.events : {};
  const override = events[event_type];

  if ( typeof override === 'string' && override.trim() ) return override.trim();
  if ( isObject( override ) )
  {
    const value = provider === OfferAnalyticsProvider.AppsFlyer
      ? override.appsflyer ?? override.appsFlyer
      : override.athena ?? override.afina;
    if ( typeof value === 'string' && value.trim() ) return value.trim();
  }

  const fallback = provider === OfferAnalyticsProvider.AppsFlyer
    ? analytics_cfg.appsflyer ?? analytics_cfg.appsFlyer
    : analytics_cfg.athena ?? analytics_cfg.afina;
  if ( typeof fallback === 'string' && fallback.trim() ) return fallback.trim();

  return DEFAULT_APPS_FLYER_EVENT_BY_TYPE[event_type];
}

function buildProperties(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Record<string, unknown>
{
  const purchase_status = PURCHASE_STATUS_BY_EVENT[event_type];
  const placement_id = typeof extra?.placement_id === 'string' ? extra.placement_id : offer.placement?.id;
  const limit = offer.purchase_limit;

  return {
    [AnalyticsPropertyKey.TIMESTAMP]: new Date().toISOString(),
    sourceId: `offer_${offer.id}`,
    offerid: offer.id,
    offer_id: offer.id,
    entitlementkey: offer.entitlement_key,
    offertype: offer.presentation ?? offer.kind ?? 'unknown',
    offer_kind: offer.presentation ?? offer.kind ?? 'unknown',
    purchase_type: offer.purchase?.type,
    offer_event: event_type,
    offerstep: OFFER_STEP_BY_EVENT[event_type],
    // Configuration the offer was shown with: lets conversion be split by
    // visual / entry point / limit without joining the catalog afterwards.
    ...( offer.shop_section ? { shop_section: offer.shop_section } : {} ),
    ...( offer.visual ? { visual: offer.visual } : {} ),
    ...( offer.badge ? { badge: offer.badge } : {} ),
    ...( offer.price_usd !== undefined && !offer.chain ? { originalusd: offer.price_usd, priceusd: offer.price_usd } : {} ),
    ...( offer.placement?.template ? { template: offer.placement.template } : {} ),
    ...( offer.trigger ? { trigger_events: offer.trigger.events.join( ',' ) } : {} ),
    ...( offer.cycle ? { cycle_id: offer.cycle.id, cycle_slot: offer.cycle.slot } : {} ),
    ...( offer.occurrence !== undefined ? { occurrence: offer.occurrence } : {} ),
    ...( limit ? {
      purchase_limit_max: limit.max,
      purchase_limit_reset: limit.reset.type,
      ...( offer.purchases_left !== undefined ? { purchases_left: offer.purchases_left } : {} ),
      ...( offer.limit_resets_at ? { limit_resets_at: offer.limit_resets_at } : {} ),
    } : {} ),
    ...( placement_id ? { [AnalyticsPropertyKey.PLACEMENTID]: placement_id, placement_id } : {} ),
    ...( purchase_status ? { status: purchase_status } : {} ),
    af_content_id: offer.id,
    af_content_type: 'offer',
    ...extra,
  };
}

function buildAthenaProperties(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Record<string, unknown>
{
  return buildProperties( offer, event_type, extra );
}

export function buildOfferPriceAnalyticsProperties(
  price?: OfferPriceAnalyticsContext | null,
): Record<string, unknown>
{
  if ( !price ) return {};

  const price_micros = price.price_micros;
  const micros_as_number = typeof price_micros === 'string' ? Number( price_micros ) : price_micros;
  const revenue = typeof micros_as_number === 'number' && Number.isFinite( micros_as_number )
    ? micros_as_number / 1_000_000
    : undefined;

  return {
    ...( price.productid ? { productid: price.productid } : {} ),
    ...( price.formatted_price ? {
      formatted_price: price.formatted_price,
      formattedprice: price.formatted_price,
    } : {} ),
    ...( price.currency ? {
      currency: price.currency,
    } : {} ),
    ...( price_micros != null ? { price_micros } : {} ),
    ...( revenue != null ? {
      price: revenue,
      revenue,
    } : {} ),
  };
}

export async function trackOfferAnalytics(
  offer: OfferDto,
  event_type: OfferEventType,
  extra?: Record<string, unknown>,
): Promise<void>
{
  const props = buildProperties( offer, event_type, extra );
  analytics.track( 'offer', buildAthenaProperties( offer, event_type, extra ) );

  // Server validation is the authoritative source for test purchases. Athena
  // keeps the event for QA visibility, while AppsFlyer must stay production-only.
  if ( PURCHASE_STATUS_BY_EVENT[event_type]
    && ( !GlobalSettings.IS_PROD || extra?.is_test === true ) ) return;

  const af_name = readEventName( offer, event_type, OfferAnalyticsProvider.AppsFlyer );

  if ( event_type === OfferEventType.Shown )
  {
    await AppsFlyer.logEvent( OFFER_SHOWN_EVENT, {
      ...props,
    } );
  }

  if ( event_type === OfferEventType.PurchaseSuccess && extra?.entitlement_active === true )
    await AppsFlyer.logEvent( OFFER_ACCEPTED_EVENT, { ...props } );

  // af_purchase is sent by the analytics server after validation, never from the SDK,
  // even when a remote offer config maps one of these events to that name.
  if ( af_name === APPS_FLYER_PURCHASE_EVENT ) return;
  if ( event_type === OfferEventType.Shown && af_name === OFFER_SHOWN_EVENT ) return;
  if ( event_type === OfferEventType.PurchaseSuccess && af_name === OFFER_ACCEPTED_EVENT ) return;

  await AppsFlyer.logEvent( af_name, props );
}
